import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/Header';
import { FlowNavigation } from '@/components/FlowNavigation';
import './TimeSelection.scss';

interface TimeSlotWithAvailability {
    id: number;
    startTime: string;
    endTime: string;
    availableSeats: number;
    totalSeats: number;
}

interface GapTimeResponseDto {
    slotId: number;
    isReserved: boolean;
    isPast: boolean;
}

export function TimeSelection() {
    const navigate = useNavigate();
    const { isMobile } = useDevice();
    const { isLoggedIn, isLoading: authLoading, user } = useAuth();
    const [selectedTimeId, setSelectedTimeId] = useState<number | null>(null);
    const [timeSlots, setTimeSlots] = useState<TimeSlotWithAvailability[]>([]);
    const [myGapTimes, setMyGapTimes] = useState<GapTimeResponseDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // 로그인 안 되어 있으면 홈 페이지로
    useEffect(() => {
        if (!authLoading && !isLoggedIn) {
            navigate('/');
        }
    }, [authLoading, isLoggedIn, navigate]);

    useEffect(() => {
        if (!authLoading && isLoggedIn && (user?.fixedStudySeats?.length ?? 0) > 0) {
            navigate('/study-cafe/fixed-seat', { replace: true });
        }
    }, [authLoading, isLoggedIn, navigate, user]);

    // 시간대 및 공강 정보 불러오기
    useEffect(() => {
        if (authLoading || !isLoggedIn) return;

        const fetchData = async () => {
            setIsLoading(true);
            try {
                // 1. 모든 시간대 조회 (가용 좌석 수 포함)
                const timeSlotsResponse = await fetch('/api/center/gap-times/time-slots');
                if (timeSlotsResponse.ok) {
                    const data = await timeSlotsResponse.json();
                    setTimeSlots(data);
                }

                // 2. 내 오늘 공강 시간대 정보 조회 (상태 포함)
                const gapTimesResponse = await fetch('/api/center/gap-times/my-today');
                if (gapTimesResponse.ok) {
                    const data = await gapTimesResponse.json();
                    setMyGapTimes(data);
                }
            } catch (error) {
                console.error('Failed to fetch time slots:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [authLoading, isLoggedIn]);


    const handleNext = () => {
        if (!selectedTimeId) return;
        const selectedSlot = timeSlots.find(s => s.id === selectedTimeId);

        // 좌석 선택 페이지로 이동 (선택한 시간대 정보 전달)
        navigate(`/study-cafe/seat-selection?timeSlotId=${selectedTimeId}`, {
            state: {
                timeSlotId: selectedTimeId,
                timeSlotLabel: selectedSlot ? `${selectedSlot.startTime.substring(0, 5)} ~ ${selectedSlot.endTime.substring(0, 5)}` : ''
            }
        });
    };

    // 시간 포맷팅 (HH:mm:ss -> HH:mm)
    const formatTime = (time: string) => time.substring(0, 5);

    return (
        <div className={`time-selection-page ${isMobile ? 'time-selection-page--mobile' : ''}`}>
            <Header />

            <main className="time-selection-main">
                <div className="main-top">
                    <div className="title-area">
                        <h1 className="page-title">이용 시간 선택</h1>
                        <p className="page-subtitle">예약할 공강 시간을 선택해주세요</p>
                    </div>

                    <div className="legend">
                        <div className="legend__item">
                            <span className="legend__color legend__color--not-free"></span>
                            <span>: 비공강 시간</span>
                        </div>
                        <div className="legend__item">
                            <span className="legend__color legend__color--unavailable"></span>
                            <span>: 예약 불가 시간</span>
                        </div>
                        <div className="legend__item">
                            <span className="legend__color legend__color--selected"></span>
                            <span>: 선택한 시간</span>
                        </div>
                    </div>
                </div>

                <div className="selection-container">
                    {isLoading ? (
                        <div className="loading" aria-hidden="true" />
                    ) : (
                        <div className="time-grid">
                            {timeSlots.map((slot) => {
                                // 내 공강 목록에서 해당 시간대 찾기
                                const myGapTime = myGapTimes.find(g => g.slotId === slot.id);

                                // 1. 공강 여부
                                const isGapTime = !!myGapTime;

                                // 2. 예약 불가 여부 (DTO 정보 활용)
                                const isReserved = myGapTime?.isReserved || false;
                                const isPast = myGapTime?.isPast || false;
                                const isFull = slot.availableSeats === 0;

                                // 3. 상태 결정
                                // Unavailable (Gray): 공강이고 (지났거나, 예약했거나, 만석)
                                const isUnavailable = isGapTime && (isPast || isReserved || isFull);

                                // 4. 비공강 여부 (Not Free: Navy)
                                const isNotFree = !isGapTime;

                                // 5. 선택 여부
                                const isSelected = selectedTimeId === slot.id;

                                // 6. 비활성화 여부
                                const isDisabled = isNotFree || isUnavailable;

                                return (
                                    <button
                                        key={slot.id}
                                        className={`
                                            time-slot 
                                            ${isNotFree ? 'time-slot--not-free' : ''}
                                            ${isUnavailable ? 'time-slot--unavailable' : ''}
                                            ${isSelected ? 'time-slot--selected' : ''}
                                        `}
                                        disabled={isDisabled}
                                        onClick={() => setSelectedTimeId(slot.id)}
                                    >
                                        <span className="time-slot__time">
                                            {slot.startTime.substring(0, 5)} ~ {slot.endTime.substring(0, 5)}
                                        </span>
                                        {!isDisabled && (
                                            <span className="time-slot__seats">
                                                ({slot.availableSeats}/{slot.totalSeats})
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>

            <FlowNavigation
                onPrev={() => navigate('/selection')}
                onNext={handleNext}
                nextLabel="다음"
                isNextDisabled={!selectedTimeId}
            />
        </div>
    );
}
